// Activity log — one endpoint for every role; the server decides the scope.
//
// A superadmin sees every company, an admin or PM sees theirs, a tester or
// developer sees their own actions plus anything on a project they're assigned
// to. There is nothing to pass here to choose that: asking for a wider scope
// than your role allows simply returns less.
//
// Filtering and paging are SERVER-side. The old admin page pulled a hard-capped
// 500 rows and filtered them in the browser, so the dropdowns only ever offered
// values that happened to be in that page, and "Showing 500 of 500" was printed
// whether or not the cap had cut anything off.

import { api, qs, filenameFromDisposition, getApiBase, getToken } from "./client";

/** One page of the log.
 *
 * @param {object} params days, action, actor, target, project_id, search, limit, offset
 * @returns {Promise<{entries: Array, total: number, limit: number, offset: number,
 *                    facets: {actions: Array, actors: Array, targets: Array}}>}
 *   `facets` is computed over your whole scope, not the current page — so
 *   narrowing one filter never empties the options in the others.
 */
export const getAuditLog = (params = {}) => api.get(`/audit/${qs(params)}`);

/** Download the log under the same filters as the screen, in `format`.
 *
 * `params` is the same filter object passed to getAuditLog, minus limit/offset
 * (the export has its own server-side row cap instead of a page size).
 *
 * `tz_offset` is added here rather than by every caller: the server renders the
 * stored UTC instant into THIS reader's wall clock so the file matches what the
 * page showed them. Minutes east of UTC, the same sign convention as
 * lib/dates.js:localUtcOffset (JS's getTimezoneOffset is backwards).
 */
export async function exportAuditLog(params = {}, format = "csv") {
  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const tzOffset = -new Date().getTimezoneOffset();
  const url = new URL(
    getApiBase() + `/audit/export${qs({ ...params, format, tz_offset: tzOffset })}`, origin);
  const token = getToken();
  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const name = filenameFromDisposition(
    res.headers.get("Content-Disposition") || "", `activity-log.${format}`);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}
